import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronUp, ChevronDown, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  getCollection, createGroup, deleteGroup, moveGroup, moveStudy, moveStudies,
  ApiError, type PlacedStudy,
} from '../../features/collections/api';

/** The ungrouped band is a real place, and it is where filing without choosing
 *  a group puts things — so it renders FIRST, above the groups. */
const UNGROUPED = '__ungrouped__';

export function CollectionDetailPage() {
  const { publicId = '' } = useParams();
  const qc = useQueryClient();
  const [groupName, setGroupName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['collection', publicId],
    queryFn: () => getCollection(publicId),
    enabled: Boolean(publicId),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['collection', publicId] });
  const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : 'Something went wrong.');
  const ok = () => { setError(null); invalidate(); };

  const addGroup = useMutation({
    mutationFn: () => createGroup(publicId, groupName.trim()),
    onSuccess: () => { setGroupName(''); ok(); },
    onError: fail,
  });
  const removeGroup = useMutation({
    mutationFn: (id: string) => deleteGroup(publicId, id),
    onSuccess: ok,
    // A non-empty group is refused with the instruction attached: move the
    // contents out first. Surfaced verbatim rather than paraphrased.
    onError: fail,
  });
  const reorderGroup = useMutation({
    mutationFn: (v: { id: string; afterId?: string; beforeId?: string }) =>
      moveGroup(publicId, v.id, { afterId: v.afterId, beforeId: v.beforeId }),
    onSuccess: ok,
    onError: fail,
  });
  // Study operations name the study by public id; only the link into the
  // study page below still uses the UID, which is how that route is addressed.
  const reorderStudy = useMutation({
    mutationFn: (v: { id: string; afterId?: string; beforeId?: string }) =>
      moveStudy(v.id, { afterId: v.afterId, beforeId: v.beforeId }),
    onSuccess: ok,
    onError: fail,
  });
  const emptyGroup = useMutation({
    mutationFn: (ids: string[]) => moveStudies(ids, publicId),
    onSuccess: ok,
    onError: fail,
  });

  // Bands: ungrouped first, then the groups in their own order.
  const bands = useMemo(() => {
    if (!data) return [];
    const byGroup = new Map<string, PlacedStudy[]>();
    for (const s of data.studies) {
      const k = s.group_public_id ?? UNGROUPED;
      (byGroup.get(k) ?? byGroup.set(k, []).get(k)!).push(s);
    }
    return [
      { id: UNGROUPED, name: 'Ungrouped', studies: byGroup.get(UNGROUPED) ?? [] },
      ...data.groups.map((g) => ({ id: g.public_id, name: g.name, studies: byGroup.get(g.public_id) ?? [] })),
    ];
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!data) return <p className="p-4 text-sm text-muted-foreground">Not found.</p>;

  const groups = data.groups;
  /**
   * Whether this person may CHANGE this collection: an admin or editor of its
   * workspace, per the server. `=== true` because the field is optional, and a
   * missing value must read as "no" rather than as permission.
   *
   * This shell had no role gating at all — Add group, the reorder chevrons,
   * Move contents out and Delete group were drawn for everyone, and the server
   * answered each with a 404 for a collection plainly on screen.
   */
  const editable = data.editable === true;
  // A collection with NO groups is the normal case, so the ungrouped band is
  // rendered without a heading — a lone "Ungrouped" label above a flat list is
  // noise when there is nothing to be ungrouped from.
  const flat = groups.length === 0;

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <Link to="/collections" className="text-sm text-muted-foreground hover:underline">Collections</Link>
        <span className="text-muted-foreground">/</span>
        <h2 className="font-medium">{data.name}</h2>
        <Badge variant="secondary">{data.total} {data.total === 1 ? 'slide' : 'slides'}</Badge>
        <div className="flex-1" />
      </div>

      {editable && (
        <div className="flex items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">New group</label>
            <Input
              value={groupName}
              placeholder="Chapter 1"
              className="w-60"
              onChange={(e) => setGroupName(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && groupName.trim() && addGroup.mutate()}
            />
          </div>
          <Button variant="secondary" disabled={!groupName.trim()} onClick={() => addGroup.mutate()}>
            <Plus className="mr-1 h-4 w-4" /> Add group
          </Button>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {data.total > data.limit && (
        <p className="text-xs text-muted-foreground">
          Showing the first {data.limit} of {data.total} slides.
        </p>
      )}

      <div className="flex flex-col gap-5">
        {bands.map((band, bi) => {
          const isUngrouped = band.id === UNGROUPED;
          if (isUngrouped && flat && band.studies.length === 0) return null;
          const gi = groups.findIndex((g) => g.public_id === band.id);
          return (
            <section key={band.id} className="flex flex-col gap-1">
              {!(isUngrouped && flat) && (
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium">{band.name}</h3>
                  <span className="text-xs text-muted-foreground">{band.studies.length}</span>
                  {!isUngrouped && editable && (
                    <>
                      {/* Reorder by naming NEIGHBOURS — the server computes the
                          new position, so one row is written per move. */}
                      <Button
                        variant="ghost" size="sm" title="Move up" disabled={gi <= 0}
                        onClick={() => reorderGroup.mutate({ id: band.id, beforeId: groups[gi - 1]?.public_id })}
                      ><ChevronUp className="h-4 w-4" /></Button>
                      <Button
                        variant="ghost" size="sm" title="Move down" disabled={gi < 0 || gi >= groups.length - 1}
                        onClick={() => reorderGroup.mutate({ id: band.id, afterId: groups[gi + 1]?.public_id })}
                      ><ChevronDown className="h-4 w-4" /></Button>
                      {band.studies.length > 0 && (
                        // The non-destructive alternative, offered right where
                        // the destructive one is — so "I just wanted the group
                        // gone" and "I wanted the slides gone" stay distinct.
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => emptyGroup.mutate(band.studies.map((s) => s.public_id))}
                        >Move contents out</Button>
                      )}
                      <Button
                        variant="ghost" size="sm" title="Delete group"
                        onClick={() => {
                          const n = band.studies.length;
                          // Since 2026-09-09 a group deletes WITHOUT its slides:
                          // they drop to the ungrouped band of this collection.
                          const what = n === 0
                            ? `Delete "${band.name}"?`
                            : `Delete group "${band.name}"? Its ${n} ${n === 1 ? 'slide stays' : 'slides stay'} in this collection, ungrouped.`;
                          if (window.confirm(what)) removeGroup.mutate(band.id);
                        }}
                      ><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                </div>
              )}
              {band.studies.length === 0 ? (
                <p className="text-xs text-muted-foreground">Empty.</p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {band.studies.map((s, i) => (
                    <li key={s.public_id} className="flex items-center gap-2 rounded border px-3 py-2">
                      <Link to={`/studies/${s.study_instance_uid}`} className="min-w-0 flex-1 truncate text-sm hover:underline">
                        {s.study_instance_uid}
                      </Link>
                      {editable && (
                        <>
                          <Button
                            variant="ghost" size="sm" title="Move up" disabled={i === 0}
                            onClick={() => reorderStudy.mutate({
                              id: s.public_id,
                              beforeId: band.studies[i - 1]?.public_id,
                            })}
                          ><ChevronUp className="h-4 w-4" /></Button>
                          <Button
                            variant="ghost" size="sm" title="Move down" disabled={i === band.studies.length - 1}
                            onClick={() => reorderStudy.mutate({
                              id: s.public_id,
                              afterId: band.studies[i + 1]?.public_id,
                            })}
                          ><ChevronDown className="h-4 w-4" /></Button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {bi === 0 && !flat && <div className="border-b" />}
            </section>
          );
        })}
      </div>
    </div>
  );
}
